const CART_UPDATE_EVENT = 'cart:update';

class CartSmartAnnouncement {
  constructor(element) {
    this.element = element;
    this.message = element.querySelector('[data-cart-smart-message]');
    this.checkout = element.querySelector('[data-cart-smart-checkout]');
    this.abortController = null;
    this.lastState = element.dataset.initialState || '';

    document.addEventListener(CART_UPDATE_EVENT, this.handleCartUpdate);
    this.checkout?.addEventListener('click', this.handleCheckoutClick);
    this.trackState(this.lastState);
  }

  handleCartUpdate = () => {
    this.refresh();
  };

  handleCheckoutClick = () => {
    this.track('wild_yard_announcement_checkout_click', {
      announcement_state: this.lastState,
    });
  };

  async refresh() {
    this.abortController?.abort();
    this.abortController = new AbortController();

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' },
        signal: this.abortController.signal,
      });

      if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
      this.render(await response.json());
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.warn('[Cart smart announcement]', error);
      }
    }
  }

  render(cart) {
    const threshold = Number(this.element.dataset.freeShippingThreshold);
    const remaining = Math.max(0, threshold - cart.total_price);
    let state = 'empty';
    let template = this.element.dataset.emptyText;

    if (cart.item_count > 0 && remaining === 0) {
      state = 'free_shipping';
      template = this.element.dataset.freeShippingText;
    } else if (cart.item_count > 0) {
      state = 'in_progress';
      template = this.element.dataset.cartText;
    }

    this.setMessage(template, remaining, state);
    this.checkout?.toggleAttribute('hidden', state !== 'free_shipping');
    this.element.dataset.state = state;

    if (state !== this.lastState) {
      this.lastState = state;
      this.trackState(state);
    }
  }

  setMessage(template, remaining, state) {
    if (!this.message) return;

    const text = String(template || '');
    const marker = '[amount]';
    const index = text.indexOf(marker);
    const emphasizedTerms = state === 'empty' ? ['Free shipping'] : ['free shipping'];

    this.message.replaceChildren();

    if (index === -1) {
      this.appendTextWithEmphasis(text, emphasizedTerms);
      return;
    }

    this.appendTextWithEmphasis(text.slice(0, index), emphasizedTerms);

    const amount = document.createElement('strong');
    amount.className = 'announcement-bar__amount';
    amount.textContent = this.formatMoney(remaining);
    this.message.append(amount);

    this.appendTextWithEmphasis(text.slice(index + marker.length), emphasizedTerms);
  }

  appendTextWithEmphasis(text, terms) {
    let remainingText = text;

    while (remainingText) {
      const nextMatch = terms
        .map((term) => ({ term, index: remainingText.toLowerCase().indexOf(term.toLowerCase()) }))
        .filter(({ index }) => index >= 0)
        .sort((a, b) => a.index - b.index)[0];

      if (!nextMatch) {
        this.message.append(remainingText);
        return;
      }

      if (nextMatch.index > 0) {
        this.message.append(remainingText.slice(0, nextMatch.index));
      }

      const matchedText = remainingText.slice(nextMatch.index, nextMatch.index + nextMatch.term.length);
      this.message.append(this.createEmphasis(matchedText));
      remainingText = remainingText.slice(nextMatch.index + nextMatch.term.length);
    }
  }

  createEmphasis(text) {
    const emphasis = document.createElement('strong');
    emphasis.className = 'announcement-bar__emphasis';
    emphasis.textContent = text;
    return emphasis;
  }

  formatMoney(cents) {
    const dollars = cents / 100;

    try {
      return new Intl.NumberFormat(this.element.dataset.locale || 'en', {
        style: 'currency',
        currency: this.element.dataset.currency || 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(dollars);
    } catch (_error) {
      return `$${dollars}`;
    }
  }

  trackState(state) {
    if (!state) return;
    this.track('wild_yard_announcement_state_view', {
      announcement_state: state,
    });
  }

  track(name, detail) {
    try {
      window.Shopify?.analytics?.publish?.(name, detail);
      window.gtag?.('event', name, detail);
      window.clarity?.('event', name);
    } catch (_error) {
      // Analytics must never interfere with the announcement or checkout.
    }
  }
}

document.querySelectorAll('[data-cart-smart-announcement]').forEach((element) => {
  if (element.dataset.cartSmartReady === 'true') return;
  element.dataset.cartSmartReady = 'true';
  new CartSmartAnnouncement(element);
});
